import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Check, CheckCheck, Trash2, LogOut, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const NOTIFICATION_TYPES = [
  { value: "all", label: "All Types" },
  { value: "sl_hit", label: "Stop Loss" },
  { value: "target_hit", label: "Target Hit" },
  { value: "new_signal", label: "New Entry" },
];

export const NotificationBell = ({ onCloseTrade }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const panelRef = useRef(null);
  const bellRef = useRef(null);

  // Fetch accounts for filter dropdown
  const fetchAccounts = useCallback(async () => {
    try {
      const response = await api.get("/accounts");
      setAccounts(response.data || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  }, []);

  // Handle "Close Trade" action from SL notification
  const handleCloseTradeAction = (notification) => {
    const { symbol, metadata } = notification;
    const { account_id, signal_id, direction } = metadata || {};
    
    // Navigate to trades page with the trade to close pre-selected
    setIsOpen(false);
    navigate(`/trades?action=close&symbol=${encodeURIComponent(symbol)}&account_id=${account_id || ''}&signal_id=${signal_id || ''}`);
  };

  // Filter notifications based on selected filters
  const filteredNotifications = notifications.filter(n => {
    const typeMatch = typeFilter === "all" || n.type === typeFilter;
    const accountMatch = accountFilter === "all" || n.account_id === accountFilter || n.account_name === accountFilter;
    return typeMatch && accountMatch;
  });

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/in-app-notifications");
      if (response.data.status) {
        setNotifications(response.data.data || []);
        setUnreadCount(response.data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread count only (for polling)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get("/in-app-notifications/unread-count");
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, []);

  // Initial load and polling
  useEffect(() => {
    fetchNotifications();
    fetchAccounts();
    
    // Poll for new notifications every 30 seconds
    const pollInterval = setInterval(fetchUnreadCount, 30000);
    
    return () => clearInterval(pollInterval);
  }, [fetchNotifications, fetchUnreadCount, fetchAccounts]);

  // Refetch when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the notification panel
      const isInsidePanel = panelRef.current && panelRef.current.contains(event.target);
      // Check if click is on the bell button
      const isOnBell = bellRef.current && bellRef.current.contains(event.target);
      // Check if click is inside a Select dropdown (rendered in portal)
      const isInsideSelect = event.target.closest('[data-radix-popper-content-wrapper]') ||
                             event.target.closest('[role="listbox"]') ||
                             event.target.closest('[data-radix-select-viewport]');
      
      if (!isInsidePanel && !isOnBell && !isInsideSelect) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/in-app-notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.put("/in-app-notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId) => {
    try {
      await api.delete(`/in-app-notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Update unread count if it was unread
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error dismissing notification:", error);
    }
  };

  // Dismiss all notifications
  const dismissAll = async () => {
    try {
      await api.delete("/in-app-notifications");
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error("Error dismissing all:", error);
    }
  };

  // Get notification type color
  const getTypeColor = (type, status) => {
    if (status === "filled") return "bg-profit";
    if (type === "new_signal") return "bg-blue-500";
    if (type === "signal_exit") return "bg-profit";
    if (type === "sl_hit") return "bg-loss";
    if (type === "target_hit") return "bg-profit";
    if (type === "signal_modified") return "bg-yellow-500";
    return "bg-slate-500";
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      filled: { text: "Filled", class: "bg-profit/20 text-profit border-profit/30" },
      submitted: { text: "Submitted", class: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
      cancelled: { text: "Cancelled", class: "bg-loss/20 text-loss border-loss/30" },
      new: { text: "New", class: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" }
    };
    return badges[status] || badges.new;
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        ref={bellRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-loss text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] bg-charcoal border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="notifications-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/20">
            <h3 className="text-white font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <>
                  <button
                    onClick={markAllAsRead}
                    className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                    title="Mark all as read"
                  >
                    <CheckCheck className="w-4 h-4" />
                  </button>
                  <button
                    onClick={dismissAll}
                    className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                    title="Clear all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="px-4 py-2 border-b border-white/10 bg-black/10 flex gap-2">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 text-xs bg-slate-800 border-slate-600 text-white w-32">
                <Filter className="w-3 h-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {NOTIFICATION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value} className="text-white text-xs hover:bg-slate-700">
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={accountFilter} onValueChange={setAccountFilter}>
              <SelectTrigger className="h-8 text-xs bg-slate-800 border-slate-600 text-white flex-1">
                <SelectValue placeholder="All Accounts" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="all" className="text-white text-xs hover:bg-slate-700">All Accounts</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.name} className="text-white text-xs hover:bg-slate-700">
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[calc(70vh-120px)]">
            {loading && notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-400 border-t-transparent mx-auto mb-2"></div>
                Loading...
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{notifications.length === 0 ? "No notifications yet" : "No notifications match filters"}</p>
                <p className="text-xs mt-1">Signal alerts will appear here</p>
              </div>
            ) : (
              filteredNotifications.map((notification) => {
                const statusBadge = getStatusBadge(notification.status);
                
                return (
                  <div
                    key={notification.id}
                    className={`flex border-b border-white/5 hover:bg-white/5 transition-colors ${
                      !notification.read ? "bg-white/5" : ""
                    }`}
                    onClick={() => !notification.read && markAsRead(notification.id)}
                  >
                    {/* Color Bar */}
                    <div className={`w-1 ${getTypeColor(notification.type, notification.status)}`}></div>
                    
                    {/* Content */}
                    <div className="flex-1 px-3 py-3">
                      {/* Time and Status Row */}
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-500">
                          {notification.created_at_ist}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded border ${statusBadge.class}`}>
                          {statusBadge.text}
                        </span>
                      </div>
                      
                      {/* Account Name */}
                      {notification.account_name && (
                        <p className="text-xs text-cyan-400 font-medium mb-1">{notification.account_name}</p>
                      )}
                      
                      {/* Strategy */}
                      {notification.strategy && (
                        <p className="text-xs text-slate-400 mb-1">{notification.strategy}</p>
                      )}
                      
                      {/* Symbol */}
                      <p className="text-white font-medium text-sm">{notification.symbol}</p>
                      
                      {/* Amount */}
                      {notification.amount !== null && notification.amount !== undefined && (
                        <p className={`text-lg font-bold ${notification.amount >= 0 ? 'text-profit' : 'text-loss'}`}>
                          ₹{Math.abs(notification.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </p>
                      )}
                      
                      {/* Message */}
                      <p className="text-xs text-slate-400 mt-1">{notification.message}</p>
                      
                      {/* Close Trade Action Button for SL Hit notifications */}
                      {notification.type === "sl_hit" && notification.metadata?.action === "close_trade" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                            handleCloseTradeAction(notification);
                          }}
                          className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-loss/20 hover:bg-loss/30 text-loss border border-loss/30 rounded text-xs font-medium transition-colors"
                          data-testid="close-trade-action-btn"
                        >
                          <LogOut className="w-3.5 h-3.5" />
                          Close Trade
                        </button>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col justify-center pr-2 gap-1">
                      {!notification.read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          className="p-1 rounded hover:bg-white/10 text-slate-500 hover:text-profit transition-colors"
                          title="Mark as read"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissNotification(notification.id);
                        }}
                        className="p-1 rounded hover:bg-white/10 text-slate-500 hover:text-loss transition-colors"
                        title="Dismiss"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
